// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, map } from 'rxjs';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import {
  TimeFromNowPipe,
  isValidDate,
} from 'src/app/modules/shared/pipes/localizedDate.pipe';
import {
  APICallState,
  ChangeInfo,
} from 'src/app/modules/shared/utils/api-call-state.util';

type TData = { lastRefreshDateTime?: string; latestUpdateInMinutes?: number };
@Component({
  selector: 'app-lastDataSourceRefreshTubeCoil',
  templateUrl: './lastDataSourceRefreshTubeCoil.component.html',
  styleUrls: ['./lastDataSourceRefreshTubeCoil.component.css'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgIf, AsyncPipe, TranslateModule, TimeFromNowPipe],
})
export class LastDataSourceRefreshTubeCoilComponent
  implements OnInit, OnChanges
{
  @Input({ required: true }) systemDetails!: SystemDetailsModel;

  // title for display
  @Input({ required: true }) title!: string;

  @Input() isOutdatedThresholdInHours = 24;

  // you can pass in tubeService.dataSourceUpdateStatus or coilService.dataSourceUpdateStatus!
  @Input({ required: true }) endpoint!: APICallState<
    {
      systemSerialNumber: string;
      systemMaterialNumber: number;
    },
    TData
  >;

  // will be definitively assigned in ngOnInit
  isOutdated$!: Observable<boolean>;
  // will be definitively assigned in ngOnInit
  data$!: Observable<TData>;

  refreshed$: Observable<ChangeInfo<TData>> = new Observable();

  ngOnInit(): void {
    this.data$ = this.endpoint.lastSuccessfulData$.pipe(
      map(({ lastRefreshDateTime, latestUpdateInMinutes }) => {
        if (latestUpdateInMinutes) {
          return {
            lastRefreshDateTime: moment()
              .subtract(latestUpdateInMinutes, 'minutes')
              .toISOString(),
          };
        } else {
          return { lastRefreshDateTime };
        }
      })
    );
    //this.refreshed$ = this.endpoint.lastSuccessfulRetry$;
    this.isOutdated$ = this.data$.pipe(
      map(({ lastRefreshDateTime }) => {
        if (!isValidDate(lastRefreshDateTime ?? null)) {
          return false;
        }
        const hoursSinceRefresh = moment().diff(
          moment(lastRefreshDateTime),
          'hours'
        );
        return hoursSinceRefresh > this.isOutdatedThresholdInHours;
      })
    );
  }

  ngOnChanges(): void {
    this.endpoint.request({
      systemMaterialNumber: this.systemDetails.systemMaterialNumber,
      systemSerialNumber: this.systemDetails.systemSerialNumber,
    });
  }
}
